/* eslint-disable react/no-find-dom-node, react/no-string-refs */
import React from 'react';
import PropTypes from 'prop-types';
import reactdom from 'react-dom';
import { intlShape } from 'react-intl';
import TextareaAutosize from 'react-autosize-textarea';
import RenameIcon from '../../common/icons/rename/RenameComponent';
import messages from '../../../data/products';
import AnalyticsClient from '../../../clients/AnalyticsClient';
import productEvents from '../../../analytics/events/products';

import './Rename.scss';

const MAX_LENGTH = 100;

/**
 * Allows the Product Tile title to be editable
 */
class ProductRenameComponent extends React.Component {
  constructor({ content }) {
    super();

    this.state = {
      content,
      initialContent: content,
      errorToastShown: false,
      active: false,
      showingTextarea: false,
    };

    this.keyDown = this.keyDown.bind(this);
    this.blur = this.blur.bind(this);
    this.mouseLeave = this.mouseLeave.bind(this);
    this.click = this.click.bind(this);
    this.setActive = this.setActive.bind(this);
    this.setShowingTextarea = this.setShowingTextarea.bind(this);
    this.change = this.change.bind(this);
    this.showMaxLengthErrorToast = this.showMaxLengthErrorToast.bind(this);
  }

  /* ----- EVENTS ----- */

  keyDown(e) {
    if (e.keyCode === 13) {
      // enter key press
      e.preventDefault();
      e.target.blur();
    }
  }

  mouseLeave() {
    if (!this.state.active && this.state.showingTextarea) {
      this.setShowingTextarea(false);
    }
  }

  blur() {
    if (!this.state.active && this.state.showingTextarea) {
      this.setShowingTextarea(false);
      return;
    }

    const text = this.getNodeContent();
    const isValidLength = this.validationLength(text);
    const isValidZero = this.validationZero(text);
    const isValidContentChange = this.validationChanged();

    if (isValidLength && isValidContentChange && isValidZero) {
      this.props.actions.nameUpdate(this.props.productId, this.props.projectId, text, this.props.runtimeConfig.api);
      if (this.state.errorToastShown) {
        this.props.actions.clearAllToast();
      }
      this.setState({ errorToastShown: false });
    } else if (!isValidZero) {
      this.setState({ content: this.state.initialContent });
      this.setNodeContent(this.state.initialContent);
      this.getNode().blur();
    }
    this.setActive(false);
    this.setShowingTextarea(false);
  }

  click() {
    const editableBox = this.getNode();

    editableBox.focus();
    this.setActive();
  }

  change(e) {
    if (!this.validationLength(e.target.value)) {
      this.showMaxLengthErrorToast();
    } else {
      this.setState({ content: e.target.value });
    }
  }
  /* ----- VALIDATION METHODS ----- */

  validationChanged() {
    if (this.getNodeContent() !== null) {
      return this.getNodeContent() !== this.state.initialContent;
    }
    return false;
  }

  validationLength(text) {
    if (text.length <= MAX_LENGTH) {
      return true;
    }
    AnalyticsClient.trackEvent(productEvents.renameError('Rename_err_max_chars'));
    return false;
  }

  validationZero(text) {
    if (text.length > 0) {
      return true;
    }
    AnalyticsClient.trackEvent(productEvents.renameError('Rename_err_min_chars'));
    return false;
  }

  /* ----- GENERAL HELPER METHODS ----- */

  showMaxLengthErrorToast() {
    if (!this.state.errorToastShown) {
      this.setState({ errorToastShown: true });
      this.props.actions.showToast({
        error: true,
        message: this.context.intl.formatMessage(messages.renameProductValidationError), // Error! Name must be less than 100 characters
      });
      this.setNodeContent(this.state.content);
    }
  }

  setActive(active = true) {
    this.setState({ active });
  }

  setShowingTextarea(showingTextarea = true) {
    this.setState({ showingTextarea });
  }

  /* ----- REACTDOM HELPER METHODS ----- */

  getNode() {
    return reactdom.findDOMNode(this.refs.rename);
  }

  getNodeContent() {
    return reactdom.findDOMNode(this.refs.rename).value;
  }

  setNodeContent(text) {
    reactdom.findDOMNode(this.refs.rename).value = text;
  }

  /* ----- LIFECYCLE METHODS ----- */

  UNSAFE_componentWillMount() {
    this.setState({ initialContent: this.props.content, content: this.props.content });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({ initialContent: nextProps.content, content: nextProps.content });
  }

  render() {
    const focusedClass =
      this.state.active && !this.props.isDisabled ? 'product-rename-component__container--active' : '';
    const enabledClass = !this.props.isDisabled ? 'product-rename-component--enabled' : '';
    return (
      <div
        className={`product-rename-component__container ${focusedClass} ${this.state.showingTextarea ? 'hover' : ''}`}
      >
        <span
          className={`product-rename-component ${enabledClass} ${this.state.showingTextarea ? 'hidden' : ''}`}
          onMouseEnter={() => this.setShowingTextarea()}
        >
          {this.state.content}
        </span>
        <TextareaAutosize
          className={`product-rename-component textarea ${enabledClass} ${!this.state.showingTextarea ? 'hidden' : ''}`}
          ref="rename"
          value={this.state.content}
          disabled={this.props.isDisabled}
          onKeyDown={this.keyDown}
          onBlur={this.blur}
          onMouseLeave={() => this.mouseLeave()}
          onFocus={() => this.setActive()}
          onChange={this.change}
          onClick={this.click}
        />
        {
          // <span onClick={this.click} className={`${!this.state.showingTextarea ? 'hidden' : 'hover'}`}><RenameIcon /></span>
        }
        <span onClick={this.click}>
          <RenameIcon />
        </span>
        {/* NOTE using the hover class instead of using CSS enables the hover styling to look ok on Firefox v55.0.
          It's a workaround until we can refactor and simplify the component */}
      </div>
    );
  }
}

ProductRenameComponent.displayName = 'ProductRename';

ProductRenameComponent.propTypes = {
  productId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    clearAllToast: PropTypes.func.isRequired,
    nameUpdate: PropTypes.func.isRequired,
    showToast: PropTypes.func.isRequired,
  }).isRequired,
  runtimeConfig: PropTypes.shape({
    api: PropTypes.string.isRequired,
  }).isRequired,
};

ProductRenameComponent.contextTypes = {
  intl: intlShape.isRequired,
};

export default ProductRenameComponent;
